import React, { useState } from 'react';
import { Building2, Users, Calendar, MoreVertical, Edit, Trash2, Settings } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

export default function CompaniesOverview({ companies, onRefresh }) {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showActions, setShowActions] = useState(null);

  const handleStatusUpdate = async (companyId, newStatus) => {
    try {
      const { error } = await supabase?.from('companies')?.update({ status: newStatus })?.eq('id', companyId);

      if (error) throw error;

      onRefresh?.();
      setShowActions(null);
    } catch (error) {
      console.error('Error updating company status:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trial':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {companies?.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Companies Found</h3>
          <p className="text-gray-500">Start by creating your first company.</p>
        </div>
      ) : (
        <div className="overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-4">Company</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Users</div>
              <div className="col-span-2">Created</div>
              <div className="col-span-1">Files</div>
              <div className="col-span-1">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {companies?.map((company, index) => (
              <div key={company?.id || index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Company Info */}
                  <div className="col-span-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{company?.name}</div>
                        <div className="text-sm text-gray-500">{company?.slug}</div>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(company?.status)}`}>
                      {company?.status}
                    </span>
                  </div>

                  {/* User Count */}
                  <div className="col-span-2">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {company?.user_profiles?.length || 0} users
                      </span>
                    </div>
                  </div>

                  {/* Created Date */}
                  <div className="col-span-2">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {formatDate(company?.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Files Count */}
                  <div className="col-span-1">
                    <span className="text-sm text-gray-900">
                      {company?.file_uploads?.length || 0}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 relative">
                    <button
                      onClick={() => setShowActions(showActions === company?.id ? null : company?.id)}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-400" />
                    </button>

                    {showActions === company?.id && (
                      <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-48">
                        <button 
                          onClick={() => {/* Edit functionality */}}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit Company</span>
                        </button>
                        
                        <button 
                          onClick={() => {/* Settings functionality */}}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Settings className="w-4 h-4" />
                          <span>Company Settings</span>
                        </button>

                        <div className="border-t border-gray-200 my-1"></div>

                        {company?.status !== 'active' && (
                          <button 
                            onClick={() => handleStatusUpdate(company?.id, 'active')}
                            className="w-full px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50 flex items-center space-x-2"
                          >
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span>Activate</span>
                          </button>
                        )}

                        {company?.status !== 'suspended' && (
                          <button 
                            onClick={() => handleStatusUpdate(company?.id, 'suspended')}
                            className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                          >
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            <span>Suspend</span>
                          </button>
                        )}

                        <div className="border-t border-gray-200 my-1"></div>

                        <button 
                          onClick={() => {/* Delete functionality with confirmation */}}
                          className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Company</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )) || (
              <div className="px-6 py-8 text-center text-gray-500">
                No companies to display
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}